import React, { useMemo, useContext, useCallback, useEffect } from 'react';
import { format as formatFn, formatDistanceToNow, differenceInDays, differenceInBusinessDays, setDefaultOptions } from 'date-fns';

import { getDateFormats, LOCALES } from 'utils/date';
import { useCurrentUser } from 'shared-hooks/user';
import { useCurrentTeam } from 'shared-hooks/team';

export const LocaleContext = React.createContext();

export const LocaleProvider = ({ children }) => {
    const currentUser = useCurrentUser();
    const currentTeam = useCurrentTeam();

    const dateFormat = useMemo(() => currentUser?.date_format, [currentUser]);
    const timeFormat = useMemo(() => currentUser?.time_format, [currentUser]);
    const weekStart = useMemo(() => currentUser?.week_start, [currentUser]);
    const language = useMemo(() => currentUser?.language, [currentUser]);
    const ignoreWeekends = useMemo(() => Boolean(currentTeam?.ignore_weekends), [currentTeam]);

    const dateTimeFormats = useMemo(() => {
        return getDateFormats(timeFormat || '12h')[dateFormat || 'mm/dd/yyyy']
    }, [timeFormat, dateFormat]);

    const formatDate = useCallback((dateValue, format) => {
        return formatFn(dateValue, dateTimeFormats[format] || format);
    }, [dateTimeFormats]);

    const getLocale = useCallback(() => {
        const locale = LOCALES[language] || LOCALES.en;

        return {
            ...locale,
            options: {
                ...locale.options,
                weekStartsOn: weekStart || 0
            }
        };
    }, [language, weekStart]);

    const _differenceInDays = useMemo(() => {
        if (ignoreWeekends) {
            return differenceInBusinessDays;
        }

        return differenceInDays;
    }, [ignoreWeekends]);

    useEffect(() => {
        setDefaultOptions({
            locale: getLocale(),
            weekStartsOn: weekStart || 0
        });
    }, [getLocale, weekStart]);

    const locale = useMemo(() => ({
        dateTimeFormats,
        weekStart: weekStart || 0,
        timeFormat,
        language,
        getLocale,
        formatDate,
        ignoreWeekends,
        differenceInDays: _differenceInDays
    }), [dateTimeFormats, timeFormat, weekStart, language, getLocale, formatDate, ignoreWeekends, _differenceInDays]);

    return (
        <LocaleContext.Provider value={locale}>
            { children }
        </LocaleContext.Provider>
    )
};
